/**
 * The read and edit shell every configuration panel uses (docs/ux/registry.md):
 * eyebrow, optional description, the read grid, Edit; in edit mode the form,
 * Save, Cancel, the status word, a 2 px signature left border and
 * `aria-current`. One panel edits at a time; the others' Edit is refused
 * with a sentence. Save is refused with "Nothing has changed." while the
 * draft equals the read values. Errors stay inline, where the person types.
 */

import { cn } from 'cn';
import { useEffect, useId, useRef, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { NOT_SET } from './describe';

export type PanelSaveState = { saving: boolean; error: string | null; conflict: boolean; savedAt: string | null };

export type ConfigPanelProps = {
  id: string;
  eyebrow: string;
  description?: ReactNode;
  editing: boolean;
  /** False while another panel is open: Edit stays focusable and says why it is refused. */
  canEdit: boolean;
  /** The phone shows most panels read-only; no Edit at all. */
  editable?: boolean;
  offline: boolean;
  dirty: boolean;
  state: PanelSaveState;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onReload: () => void;
  read: ReactNode;
  form: ReactNode;
  headerExtra?: ReactNode;
  className?: string;
};

export function ConfigPanel({ id, eyebrow, description, editing, canEdit, editable = true, offline, dirty, state, onEdit, onCancel, onSave, onReload, read, form, headerExtra, className }: ConfigPanelProps) {
  const headingId = useId();
  const ref = useRef<HTMLElement>(null);

  // Opening the editor moves focus to its first control; Escape hands it back to Edit (the caller decides about a dirty draft).
  useEffect(() => {
    if (!editing) return;
    const first = ref.current?.querySelector<HTMLElement>('form [data-autofocus], form input, form textarea, form button[data-slot="upload-choose"], form [role="radio"], form [role="checkbox"]');
    first?.focus();
  }, [editing]);

  function onKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onCancel(); requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[data-slot="panel-edit"]')?.focus()); }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!dirty || state.saving || offline) return;
    onSave();
  }

  const statusText = offline ? 'Paused while offline. Your typed values are kept.' : state.saving ? 'Saving' : state.savedAt ? `Saved ${state.savedAt}` : 'Not saved yet';
  const saveRefused = !dirty || state.saving;

  return (
    <section ref={ref} id={id} data-slot="config-panel" data-panel={id} aria-labelledby={headingId} aria-current={editing ? 'true' : undefined} className={cn('rounded-xl border border-beam-line bg-beam-paper p-4', editing && 'border-l-2 border-l-beam-signature', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={headingId} className="eyebrow flex items-center gap-2 text-beam-text-muted">
          {eyebrow}
          {editing ? <span className="rounded-full bg-beam-cream-deep px-2 py-0.5 text-[11px] font-medium normal-case tracking-normal text-foreground">Editing</span> : null}
        </h2>
        <div className="flex items-center gap-2">
          {headerExtra}
          {editable ? (
            editing ? (
              <Button type="button" variant="ghost" size="default" className="h-8" onClick={onCancel}>Cancel</Button>
            ) : canEdit ? (
              <Button type="button" variant="ghost" size="default" className="h-8" data-slot="panel-edit" onClick={onEdit}>Edit</Button>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" variant="ghost" size="default" className="h-8 cursor-default text-beam-text-muted" data-slot="panel-edit" aria-disabled="true" aria-describedby={`${headingId}-refused`} onClick={(e) => e.preventDefault()}>Edit</Button>
                </TooltipTrigger>
                <TooltipContent id={`${headingId}-refused`}>Finish or cancel the open edit first.</TooltipContent>
              </Tooltip>
            )
          ) : null}
        </div>
      </div>
      {description ? <p className="mt-1 text-[13px] text-beam-text-muted">{description}</p> : null}
      {editing ? (
        <form aria-label={`Edit ${eyebrow.toLowerCase()}`} noValidate onSubmit={submit} onKeyDown={onKeyDown} className="mt-4 flex flex-col gap-4">
          {form}
          {state.conflict ? (
            <div role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-warning bg-beam-paper px-3 py-2 text-[13px]">
              <p>This doctor was changed by someone else since you opened this panel. Reload to see the change; your typed values are kept in this box until you do.</p>
              <Button type="button" variant="outline" size="sm" className="mt-2" onClick={onReload}>Reload</Button>
            </div>
          ) : state.error ? (
            <p role="alert" className="rounded-lg border border-l-[3px] border-beam-line border-l-beam-error bg-beam-paper px-3 py-2 text-[13px]">{state.error}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            {saveRefused ? (
              <Button type="submit" size="lg" variant="outline" className="h-9 cursor-default text-beam-text-muted hover:bg-beam-paper" disabled={offline} aria-disabled="true" aria-describedby={`${headingId}-save-status`}>{state.saving ? 'Saving' : 'Save'}</Button>
            ) : (
              <Button type="submit" size="lg" className="h-9" data-primary="true" disabled={offline} aria-describedby={`${headingId}-save-status`}>Save</Button>
            )}
            <span id={`${headingId}-save-status`} className="text-xs text-beam-text-muted">{!dirty && !state.saving && !offline ? 'Nothing has changed.' : statusText}</span>
          </div>
        </form>
      ) : (
        <div className="mt-3">{read}</div>
      )}
    </section>
  );
}

/** The read grid: label over value, two columns on desktop, one on the phone; "Not set" in the muted colour, never blank. */
export function ReadGrid({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 }) {
  return <dl className={cn('grid gap-x-6 gap-y-3', columns === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1')}>{children}</dl>;
}

export function ReadPair({ label, value, span }: { label: string; value: ReactNode | null | undefined; span?: boolean }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={cn('flex flex-col gap-0.5', span && 'md:col-span-2')}>
      <dt className="text-xs text-beam-text-muted">{label}</dt>
      <dd className={cn('text-sm md:text-sm', empty && 'text-beam-text-muted')}>{empty ? NOT_SET : value}</dd>
    </div>
  );
}

/** A field: label above, the control, help under it; the error replaces the help. */
export function Field({ label, htmlFor, help, error, optional, children, className }: { label: string; htmlFor: string; help?: ReactNode; error?: string | null; optional?: boolean; children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium">{label}{optional ? <span className="font-normal text-beam-text-muted"> (optional)</span> : null}</label>
      {children}
      {error ? <p id={`${htmlFor}-error`} role="alert" className="text-[13px] text-beam-error">{error}</p> : help ? <p id={`${htmlFor}-help`} className="text-xs text-beam-text-muted">{help}</p> : null}
    </div>
  );
}
